import { Link, NavLink, useLocation } from "react-router-dom";
import { useEffect, useState } from "react";
import { ShoppingBag, Menu, X } from "lucide-react";
import { useCart } from "@/lib/cart";
import CartDrawer from "@/components/CartDrawer";
import BrandMark from "@/components/BrandMark";

const navItems = [
  { to: "/", label: "Home" },
  { to: "/shop", label: "Shop" },
  { to: "/about", label: "Our Story" },
];

const Header = () => {
  const { count, setIsOpen } = useCart();
  const [scrolled, setScrolled] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const location = useLocation();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 20);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => {
    setMobileOpen(false);
  }, [location.pathname]);

  return (
    <>
      <header
        data-testid="site-header"
        className={`fixed top-0 inset-x-0 z-40 transition-all duration-500 ${
          scrolled
            ? "backdrop-blur-xl bg-[#0A0A0A]/80 border-b border-white/5"
            : "bg-transparent"
        }`}
      >
        <div className="max-w-[1400px] mx-auto px-6 md:px-10 h-32 flex items-center justify-between">
          <Link to="/" data-testid="header-logo" className="group">
            <BrandMark size="md" className="transition-transform duration-500 group-hover:-translate-y-0.5" />
          </Link>

          <nav className="hidden md:flex items-center gap-10">
            {navItems.map((n) => (
              <NavLink
                key={n.to}
                to={n.to}
                data-testid={`nav-${n.label.toLowerCase().replace(/\s/g, "-")}`}
                end={n.to === "/"}
                className={({ isActive }) =>
                  `text-[0.78rem] tracking-[0.2em] uppercase transition-colors duration-300 ${
                    isActive
                      ? "text-[#D4AF37]"
                      : "text-[#F5F5F0]/80 hover:text-[#D4AF37]"
                  }`
                }
              >
                {n.label}
              </NavLink>
            ))}
          </nav>

          <div className="flex items-center gap-4">
            <button
              data-testid="open-cart-btn"
              onClick={() => setIsOpen(true)}
              className="relative p-2 text-[#F5F5F0] hover:text-[#D4AF37] transition-colors"
              aria-label="Open cart"
            >
              <ShoppingBag size={20} strokeWidth={1.4} />
              {count > 0 && (
                <span
                  data-testid="cart-count-badge"
                  className="absolute -top-0.5 -right-1 min-w-[18px] h-[18px] px-1 rounded-full bg-[#D4AF37] text-[#0A0A0A] text-[10px] font-medium flex items-center justify-center"
                >
                  {count}
                </span>
              )}
            </button>

            <button
              data-testid="mobile-menu-toggle"
              onClick={() => setMobileOpen((v) => !v)}
              className="md:hidden p-2 text-[#F5F5F0]"
              aria-label="Toggle menu"
            >
              {mobileOpen ? <X size={20} /> : <Menu size={20} />}
            </button>
          </div>
        </div>

        {mobileOpen && (
          <div
            data-testid="mobile-menu"
            className="md:hidden border-t border-white/5 bg-[#0A0A0A]/95 backdrop-blur-xl"
          >
            <nav className="px-6 py-6 flex flex-col gap-5">
              {navItems.map((n) => (
                <NavLink
                  key={n.to}
                  to={n.to}
                  end={n.to === "/"}
                  data-testid={`mobile-nav-${n.label.toLowerCase().replace(/\s/g, "-")}`}
                  className={({ isActive }) =>
                    `text-sm tracking-[0.2em] uppercase ${
                      isActive ? "text-[#D4AF37]" : "text-[#F5F5F0]/80"
                    }`
                  }
                >
                  {n.label}
                </NavLink>
              ))}
            </nav>
          </div>
        )}
      </header>
      <CartDrawer />
    </>
  );
};

export default Header;
